import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";

const FIELD_SETS = {
  general: {
    title: "General Operations Enquiry",
    cta: "Discuss Your Operations",
    fields: ["name", "email", "company", "phone", "role", "message"],
  },
  eazyms: {
    title: "EazyMS Enquiry",
    cta: "Request a Demo",
    fields: ["name", "email", "company", "phone", "interest", "message"],
  },
};

const LABELS = {
  name: "Full name",
  email: "Work email",
  company: "Company",
  phone: "Phone",
  role: "Your role",
  message: "Tell us about your operations",
  origin: "Origin",
  destination: "Destination",
  cargo_type: "Cargo type",
  fleet_size: "Fleet size",
  vehicle_types: "Vehicle types",
};

export default function LeadForm({ variant = "general", theme = "default" }) {
  const config = FIELD_SETS[variant];
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const accent =
    theme === "zoom" ? "focus:ring-zoom-teal" : theme === "ms" ? "focus:ring-ms-yellow" : "focus:ring-ez-yellow";
  const btn =
    theme === "zoom"
      ? "bg-zoom-teal text-white hover:bg-zoom-text"
      : "bg-ez-text text-ez-bg hover:bg-ez-yellow hover:text-ez-text";

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { lead_type: variant, honeypot, ...data });
      setDone(true);
      toast.success("Enquiry received. Our team will be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`lead-success-${variant}`}
        className="border border-ez-text bg-ez-bg p-10 text-center flex flex-col items-center"
      >
        <CheckCircle size={48} weight="fill" className="text-ez-yellow" />
        <h3 className="font-display text-2xl uppercase tracking-tight mt-4">Enquiry received</h3>
        <p className="text-ez-text/60 mt-2 max-w-sm">
          Thank you. Our operations team will review your request and respond shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`lead-form-${variant}`} className="border border-ez-text bg-white">
      <div className="border-b border-ez-text px-6 py-4 bg-ez-bg">
        <span className="font-mono-ez text-xs uppercase tracking-widest text-ez-text/50">Form</span>
        <h3 className="font-display text-xl uppercase tracking-tight">{config.title}</h3>
      </div>
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-ez-border">
        {config.fields.map((f) => {
          const full = f === "message";
          return (
            <div key={f} className={`bg-white ${full ? "sm:col-span-2" : ""}`}>
              <label className="block px-4 pt-3 text-[11px] font-mono-ez uppercase tracking-wider text-ez-text/50">
                {LABELS[f]}
                {(f === "name" || f === "email") && <span className="text-red-500"> *</span>}
              </label>
              {full ? (
                <textarea
                  data-testid={`field-${f}`}
                  rows={4}
                  value={data[f] || ""}
                  onChange={(e) => set(f, e.target.value)}
                  className={`w-full px-4 pb-3 pt-1 bg-transparent outline-none text-sm resize-none focus:ring-2 ${accent} ring-inset`}
                />
              ) : f === "interest" ? (
                <select
                  data-testid={`field-${f}`}
                  value={data[f] || ""}
                  onChange={(e) => set(f, e.target.value)}
                  className={`w-full px-4 pb-3 pt-1 bg-transparent outline-none text-sm focus:ring-2 ${accent} ring-inset`}
                >
                  <option value="">Select…</option>
                  <option value="demo">Demo</option>
                  <option value="integration">Integration</option>
                  <option value="deployment">Deployment</option>
                </select>
              ) : (
                <input
                  data-testid={`field-${f}`}
                  type={f === "email" ? "email" : "text"}
                  value={data[f] || ""}
                  onChange={(e) => set(f, e.target.value)}
                  className={`w-full px-4 pb-3 pt-1 bg-transparent outline-none text-sm focus:ring-2 ${accent} ring-inset`}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        aria-hidden="true"
      />
      <div className="px-6 py-5 border-t border-ez-text">
        <button
          type="submit"
          disabled={loading}
          data-testid={`submit-${variant}`}
          className={`inline-flex items-center gap-2 px-7 py-3 font-semibold text-sm transition-colors disabled:opacity-50 ${btn}`}
        >
          {loading ? "Sending…" : config.cta}
          {!loading && <ArrowRight size={16} weight="bold" />}
        </button>
        <p className="mt-3 text-xs text-ez-text/40">
          By submitting you agree to our processing of this information to respond to your enquiry.
        </p>
      </div>
    </form>
  );
}
